import Chip from './Chip'
import styles from './SupportingEvidenceBlock.module.css'

/**
 * SupportingEvidenceBlock — renders an `evidence` content block: the
 * "Supporting Evidence" card at the bottom of an opportunity's detail —
 * Products + Example Cases chips on the left, a Patterns Detected bullet
 * list on the right, all enclosed in one card with its own ruled heading.
 * Split out like `comparison`, for the same reason: a bespoke, multi-part
 * layout that would bloat BlockRenderer if inlined there.
 *
 * Props mirror the `evidence` block schema:
 *   heading  {string} — e.g. "Supporting Evidence"
 *   products {string[]}
 *   cases    {string[]}
 *   patterns {string[]}
 *   accentColor {string} — optional; when set (via BlockRenderer's
 *                          accentColor, e.g. the insight modal), tints the
 *                          Products/Example Cases pills and the Patterns
 *                          Detected bullet dots with the card's accent
 *                          instead of the default blue.
 */
export default function SupportingEvidenceBlock({ heading, products = [], cases = [], patterns = [], accentColor }) {
  return (
    <div className={styles.card}>
      {heading && (
        <div className={styles.headingRow}>
          <span className={styles.heading}>{heading}</span>
          <span className={styles.headingRule} />
        </div>
      )}

      <div className={styles.columns}>
        <div className={styles.col}>
          {products.length > 0 && (
            <div className={styles.group}>
              <span className={styles.groupLabel}>Products</span>
              <div className={styles.chipGrid}>
                {products.map(label => <Chip key={label} label={label} tone="info" color={accentColor} />)}
              </div>
            </div>
          )}
          {cases.length > 0 && (
            <div className={styles.group}>
              <span className={styles.groupLabel}>Example Cases</span>
              <div className={styles.chipGrid}>
                {cases.map(label => <Chip key={label} label={label} tone="info" color={accentColor} />)}
              </div>
            </div>
          )}
        </div>

        {patterns.length > 0 && (
          <div className={styles.col}>
            <div className={styles.group}>
              <span className={styles.groupLabel}>Patterns Detected</span>
              <ul className={styles.patternList} style={accentColor ? { '--pattern-dot': accentColor } : undefined}>
                {patterns.map(p => <li key={p}>{p}</li>)}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
